import { useEffect, useRef } from 'react';
import { driver, type Config } from 'driver.js';
import 'driver.js/dist/driver.css';
import IS_EFAS from '../../../components/sitebase/Environment';

export const TUTORIAL_STORAGE_KEY = 'efas-map-tutorial-seen';

type DriverStep = NonNullable<Config['steps']>[number];

interface MapTutorialProps {
    anonymousUser: boolean;
    isSmallDisplay: boolean;
    isForecaster: boolean;
    wmsReady?: boolean;
}

export default function MapTutorial({ anonymousUser, isSmallDisplay, isForecaster, wmsReady }: MapTutorialProps) {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const buildSteps = (): DriverStep[] => {
        const steps: DriverStep[] = [
            {
                popover: {
                    title: 'Welcome to the Map Viewer',
                    description: 'This short tour covers the key features. Use the arrow buttons to navigate, or press Escape to exit.',
                },
            },
            {
                element: '#tutorial-wms-list',
                popover: {
                    title: 'Layer Browser',
                    description: 'Browse and search available data layers here. Click any layer to add it to the map.',
                },
            },
            {
                element: '#tutorial-layer-widget',
                popover: {
                    title: 'Active Layers & Forecast Date',
                    description: 'Manage your active layers — toggle visibility, adjust opacity, reorder, or remove them. Use the date selector at the top to change the forecast.',
                },
            },
            {
                element: '#map',
                popover: {
                    title: 'Interactive Map',
                    description: 'Click anywhere on the map to get point information for the active layers at that location.',
                },
            },
            {
                element: '#tutorial-search',
                popover: {
                    title: 'Location Search',
                    description: 'Search for a place by name to quickly navigate to it on the map.',
                },
            },
        ];

        if (IS_EFAS && !anonymousUser && !isSmallDisplay) {
            steps.push(
                {
                    element: '#show-active-notifications',
                    popover: {
                        title: 'Active Notifications',
                        description: 'Show active flood alert notifications on the map for the selected forecast date.',
                    },
                },
                {
                    element: '#show-feedbacks',
                    popover: {
                        title: 'Feedbacks',
                        description: 'View and submit observational feedback for ongoing flood events.',
                    },
                },
                {
                    element: '#show-missed-events',
                    popover: {
                        title: 'Missed Events',
                        description: 'Report flood events that were not captured by the forecast.',
                    },
                },
            );
        }

        if (!anonymousUser && !isSmallDisplay) {
            steps.push(
                {
                    element: '#tutorial-gfm',
                    popover: {
                        title: 'GFM Widget',
                        description: 'Open the Global Flood Monitoring widget to draw areas of interest or view satellite-derived flood reports.',
                    },
                },
                {
                    element: '#tutorial-add-wms',
                    popover: {
                        title: 'External WMS',
                        description: 'Add layers from any external WMS (Web Map Service) server.',
                    },
                },
                {
                    element: '#preset-toggle',
                    popover: {
                        title: 'Presets',
                        description: 'Save and restore your favourite map configurations — active layers, forecast date, and more.',
                    },
                },
            );
        }

        steps.push({
            element: '#tutorial-map-settings',
            popover: {
                title: 'Map Settings',
                description: 'Configure the base map, widget positions, feature info display, and other preferences.',
            },
        });

        if (!anonymousUser && isForecaster) {
            steps.push({
                element: '#cart-button',
                popover: {
                    title: 'Forecast Cart',
                    description: 'Access your data download cart to export and archive forecast data.',
                },
            });
        }

        steps.push({
            popover: {
                title: "All done!",
                description: 'You\'re ready to use the map. Click the <strong>?</strong> button at any time to replay this tour.',
            },
        });

        return steps;
    };

    const startTutorial = () => {
        const driverObj = driver({
            showProgress: true,
            animate: true,
            steps: buildSteps(),
            onDestroyed: () => {
                localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
            },
        });
        driverObj.drive();
    };

    useEffect(() => {
        if (wmsReady && !localStorage.getItem(TUTORIAL_STORAGE_KEY)) {
            timerRef.current = setTimeout(startTutorial, 300);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startTutorial is rebuilt each render (it reads anonymousUser/isSmallDisplay/isForecaster to build the tour steps); the auto-start timer is intentionally keyed only on wmsReady
    }, [wmsReady]);

    return (
        <button className="btn px-3" title="Start interactive tutorial" onClick={startTutorial}>
            ?
        </button>
    );
}
